import Header from '@/components/Header';
import Footer from '@/components/Footer';
import CustomCursor from '@/components/CustomCursor';
import { Toaster } from 'sonner';
import { 
  ArrowRight, CheckCircle, FileText, Shield, Clock, Users, 
  BarChart3, AlertTriangle, Briefcase, Target, Layers, Zap
} from 'lucide-react';

const WorkflowPage = () => {
  const workflowPhases = [
    {
      step: '1',
      title: 'Intake & Core Screening',
      goal: 'Decide if an action should move into full diligence at all.',
      items: [
        'Ingests PRs, requirement packages, data room links, and basic financials.',
        'Builds a core profile: NAICS/PSC (if applicable), jurisdictions, value, and sensitivity.',
        'Runs sanctions & restricted-party checks.',
        'Produces an initial core risk rating and "proceed / do not proceed" recommendation.'
      ]
    },
    {
      step: '2',
      title: 'Fast Scan',
      goal: 'Surface deal-killers and obvious blockers early.',
      items: [
        'Runs Legal and Budget first to confirm basic structure and constraints.',
        'Adds HR and Tech/Sec for people, systems, and data risk.',
        'Uses GREEN / YELLOW / RED with clear "what this means" language.',
        'Recommends whether to go deeper, and what specifically to Deep Dive.'
      ]
    },
    {
      step: '3',
      title: 'Deep Dive',
      goal: 'Turn risk into concrete actions, owners, and timelines.',
      items: [
        'Expands domain-specific checklists where risk is higher.',
        'Assigns owners and due dates for each remediation task.',
        'Captures remediation cost/time and downstream execution impact.',
        'Feeds a consolidated conditions-to-close register.'
      ]
    },
    {
      step: '4',
      title: 'Award Package & Portal Submission',
      goal: 'Produce a compliant, traceable package and submit with confidence.',
      items: [
        'Drafts RFI/RFQ/SOW content using FAR/DFARS-aware guidance.',
        'Assembles required attachments, summaries, and checklists into a single package.',
        'Runs completeness checks and flags missing items before submission.',
        'Prepares portal-ready submissions and logs an auditable submission trail.'
      ]
    }
  ];

  const ioTable = [
    {
      phase: 'Intake & Core Screening',
      inputs: ['PR / requirement package', 'Data room link & key docs', 'Basic financials & vendor info'],
      actions: ['Extracts entities, values, dates, and constraints', 'Runs sanctions & basic compliance checks', 'Builds a core deal/action profile and initial risk view'],
      outputs: ['Core profile summary', 'Initial risk rating & "proceed / do not proceed" recommendation', 'List of missing or unclear items']
    },
    {
      phase: 'Fast Scan',
      inputs: ['Core profile', 'Key contracts, policies, org charts', 'Configured checklists by module'],
      actions: ['Runs Legal/Budget/HR/Tech/Sec modules in waves', 'Flags deal-killers vs manageable issues', 'Suggests which areas need Deep Dive'],
      outputs: ['Fast Scan summary banner (Go / Caution / Stop)', 'Module-level risk chips (GREEN / YELLOW / RED)', 'Recommended Deep Dive scope']
    },
    {
      phase: 'Deep Dive',
      inputs: ['Fast Scan findings', 'Expanded document sets', 'Domain-specific checklists'],
      actions: ['Adds detail to each risk and issue', 'Assigns owners, due dates, and remediation paths', 'Estimates cost/time and integration/execution impact'],
      outputs: ['Updated risk scores by module', 'Conditions-to-close register', 'Draft briefing language for leadership']
    },
    {
      phase: 'Award Package & Portal Submission',
      inputs: ['Conditions-to-close list', 'Requirements, constraints, and attachments', 'Approval chain & submission rules'],
      actions: ['Drafts RFI/RFQ/SOW artifacts from structured findings', 'Performs completeness checks and flags missing items', 'Produces a portal-ready submission package and logs the trail'],
      outputs: ['Draft RFI/RFQ/SOW + review-ready redlines', 'Submission-ready package (attachments + checklist)', 'Auditable submission log (who/what/when)']
    }
  ];

  const roleViews = [
    {
      title: 'Contracting Officers & 1102s',
      subtitle: 'Day-to-day execution',
      items: [
        'Personal queue of actions by phase with due dates.',
        'Guided checklists and AI-drafted summaries at each step.',
        'RFI/RFQ/SOW draft generation with human review checkpoints.'
      ]
    },
    {
      title: 'Branch Chiefs & Supervisors',
      subtitle: 'Workload & risk supervision',
      items: [
        'Live view of actions in Intake, Fast Scan, Deep Dive, and Award Package.',
        'Workload distribution and complexity scores by CO.',
        'Flags for aging actions and high-value / high-risk submissions.'
      ]
    },
    {
      title: 'Acquisition Leadership / HCA',
      subtitle: 'Portfolio decisions',
      items: [
        'Dashboards showing cycle times by phase and organization.',
        'Top-risk deals and submission readiness hotspots.',
        'Drill-down from portfolio view to single action workspace.'
      ]
    },
    {
      title: 'Finance & Budget',
      subtitle: 'Funding & obligation risk',
      items: [
        'Visibility into which phase key funded actions sit in.',
        'Alerts when cycle time threatens obligation deadlines.',
        'Links between conditions-to-close and funding lines.'
      ]
    },
    {
      title: 'Program & Portfolio Managers',
      subtitle: 'Mission outcomes',
      items: [
        'Program-centric view of all actions and their phase.',
        'Auto-generated narrative on progress and risk.',
        'Award package outputs tied back to program milestones.'
      ]
    },
    {
      title: 'Cross-functional Diligence Teams',
      subtitle: 'Legal / Budget / HR / Tech / Integration',
      items: [
        'Shared issue register instead of scattered trackers.',
        'Clear view of who owns what across phases.',
        'Single place to document decisions and tradeoffs.'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-indigo-50">
      <CustomCursor />
      <Toaster position="top-center" />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative pt-20 pb-16 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-100/50 via-transparent to-purple-100/50"></div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block px-4 py-1.5 bg-indigo-100 text-indigo-700 text-sm font-semibold rounded-full mb-6">
                  Guided, Explainable, Audit-Ready
                </span>
                <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                  From Intake to Award Package,
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">
                    One Guided Workflow
                  </span>
                </h1>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  AcuSightPro turns the messy reality of documents, emails, and trackers into a single, repeatable process. 
                  Intake, Fast Scan, Deep Dive, and award package creation (RFI/RFQ/SOW) all live in one workspace with clear 
                  owners, deadlines, and conditions-to-close, built for FAR/DFARS-aware teams.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <a
                    href="/contact"
                    className="px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-purple-500/30 transition-all hover:-translate-y-0.5 text-center"
                  >
                    Walk Through the Workflow
                  </a>
                  <a
                    href="/product"
                    className="px-8 py-3 bg-white border-2 border-gray-200 text-gray-700 font-semibold rounded-full hover:border-purple-300 hover:text-purple-700 transition-all text-center"
                  >
                    Back to Product Overview
                  </a>
                </div>
                <p className="text-sm text-gray-500">
                  New users follow step-by-step wizards; power users jump straight to the data and decisions.
                </p>
              </div>

              {/* 4-Step Workflow Card */}
              <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-bold text-gray-900">4-Step Acquisition Flow</h3>
                  <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">
                    ASP Workflow
                  </span>
                </div>
                <div className="space-y-4">
                  {[
                    { step: '1', title: 'Intake & Core Screening', desc: 'Ingests PRs and data rooms, builds a core profile, and flags early "do not proceed" risks.' },
                    { step: '2', title: 'Fast Scan', desc: 'Runs Legal/Budget/HR/Tech/Sec modules in waves to surface deal-killers and blocking issues.' },
                    { step: '3', title: 'Deep Dive', desc: 'Expands checklists where risk is high, assigns owners, and estimates remediation cost/time.' },
                    { step: '4', title: 'Award Package & Portal Submission', desc: 'Generates RFI/RFQ/SOW outputs, builds a compliant package, and prepares portal-ready submissions.' }
                  ].map((item, index) => (
                    <div key={index} className="flex gap-4">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                        {item.step}
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm">{item.title}</h4>
                        <p className="text-xs text-gray-500">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Four Phases Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                The Four Phases of Every{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">
                  AcuSightPro Deal
                </span>
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Each phase has clear entry criteria, outputs, and handoffs. The system tracks progress, risk, and workload 
                so leadership can see exactly where every deal sits.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {workflowPhases.map((phase, index) => (
                <div
                  key={index}
                  className="bg-gray-50 rounded-2xl p-6 border-2 border-dashed border-gray-300 hover:border-purple-400 hover:bg-white hover:shadow-lg transition-all"
                  data-robot-tooltip={phase.goal}
                >
                  <div className="text-xs font-bold text-purple-600 uppercase tracking-wide mb-2">
                    Step {phase.step}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{phase.title}</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    <span className="font-medium">Goal:</span> {phase.goal}
                  </p>
                  <ul className="space-y-2">
                    {phase.items.map((item, i) => (
                      <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* RFI/RFQ/SOW + Portal Section */}
        <section className="py-16 bg-gradient-to-br from-gray-50 to-indigo-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                From Findings to{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">
                  Award-Ready Packages
                </span>
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                The workflow doesn&apos;t stop at analysis. AcuSightPro converts diligence outputs into structured acquisition 
                artifacts (RFI, RFQ, SOW) and then packages everything into a portal-ready submission, without losing traceability.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8 border border-purple-100">
              <div className="grid md:grid-cols-3 gap-6 mb-6">
                {[
                  {
                    title: 'AI RFI/RFQ/SOW Generator',
                    desc: 'Converts your inputs (requirements, constraints, risk findings) into structured drafts your team can edit and approve.',
                    icon: FileText
                  },
                  {
                    title: 'FAR/DFARS-Aware Drafting',
                    desc: 'Flags clause conflicts and compliance gaps, then suggests corrections with explainable rationale.',
                    icon: Shield
                  },
                  {
                    title: 'Automated Submission Portal',
                    desc: 'Creates a portal-ready package with attachments, naming, and completeness checks, plus a submission audit trail.',
                    icon: Zap
                  }
                ].map((item, index) => (
                  <div key={index} className="bg-gray-50 rounded-xl p-5 hover:bg-purple-50 transition-colors">
                    <item.icon className="w-8 h-8 text-purple-600 mb-3" />
                    <h4 className="font-bold text-gray-900 mb-2">{item.title}</h4>
                    <p className="text-sm text-gray-600">{item.desc}</p>
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-500 text-center">
                Outputs remain advisory and require human approval. Every draft, change, and final submission artifact is logged for oversight and audit.
              </p>
            </div>
          </div>
        </section>

        {/* Input/Output Table Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                What Goes In, What Comes Out,{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">
                  At Each Step
                </span>
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                ASP makes the workflow predictable: every phase has defined inputs, system actions, and outputs your team can 
                reuse in briefs, IG responses, and post-award reviews.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Phase</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Key Inputs</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">What ASP Does</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Outputs</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {ioTable.map((row, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-semibold text-gray-900 text-sm">{row.phase}</td>
                        <td className="px-6 py-4">
                          <ul className="text-sm text-gray-600 space-y-1">
                            {row.inputs.map((input, i) => (
                              <li key={i}>• {input}</li>
                            ))}
                          </ul>
                        </td>
                        <td className="px-6 py-4">
                          <ul className="text-sm text-gray-600 space-y-1">
                            {row.actions.map((action, i) => (
                              <li key={i}>• {action}</li>
                            ))}
                          </ul>
                        </td>
                        <td className="px-6 py-4">
                          <ul className="text-sm text-gray-600 space-y-1">
                            {row.outputs.map((output, i) => (
                              <li key={i}>• {output}</li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        {/* Role-Based Views Section */}
        <section className="py-16 bg-gradient-to-br from-gray-50 to-indigo-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                What the Workflow Looks Like for{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">
                  Each Role
                </span>
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Everyone works from the same system of record, but each persona sees the workflow through their own lens: 
                workload, risk, funding, or program outcomes.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {roleViews.map((role, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg hover:border-purple-200 transition-all"
                >
                  <h3 className="font-bold text-gray-900 mb-1">{role.title}</h3>
                  <p className="text-sm text-purple-600 mb-4">{role.subtitle}</p>
                  <ul className="space-y-2">
                    {role.items.map((item, i) => (
                      <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-purple-600 to-blue-600">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to See AcuSightPro in Action?
            </h2>
            <p className="text-lg text-purple-100 mb-8">
              We&apos;re building an acquisition copilot, diligence engine, and command center for modern agencies. 
              Let&apos;s walk you through what investors are backing.
            </p>
            <a
              href="/contact"
              className="inline-flex px-8 py-4 bg-white text-purple-700 font-semibold rounded-full hover:shadow-lg transition-all hover:-translate-y-0.5"
            >
              Contact Us
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default WorkflowPage;
